import { Component, ChangeDetectionStrategy, DestroyRef, Injector, afterNextRender, computed, effect, inject, input, output, signal, viewChild } from '@angular/core';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { Router, RouterLink } from '@angular/router';
import { NgIcon, provideIcons } from '@ng-icons/core';
import { iconoirXmark, iconoirClockRotateRight } from '@ng-icons/iconoir';
import { VoiceService } from '../../services/voice.service';
import { VoiceSessionService } from '../../services/voice-session.service';
import { VoiceGuidedFlowService } from '../../services/voice-guided-flow.service';
import { VoiceBriefingService } from '../../services/voice-briefing.service';
import { VoiceOverlayService } from '../../services/voice-overlay.service';
import { VoiceChatThreadComponent } from './voice-chat-thread/voice-chat-thread.component';
import { VoiceSessionHistoryComponent } from './voice-session-history/voice-session-history.component';
import { QUICK_ACTIONS, QuickActionFlow } from '../../constants';
import { VoiceMessageOption, VoiceMessageLink } from '../../models';
import { SubscriptionFeaturesService } from '../../../../services/subscription-features.service';
import { ASSISTANT_OVERLAY_TOUR } from '../../../../tutorials/assistant.tours';
import { TutorialButtonComponent } from '../../../../tutorials/tutorial-button/tutorial-button.component';
import { TutorialService } from '../../../../tutorials/tutorial.service';

/**
 * Overlay plein écran de l'assistant vocal conversationnel (MOKA-204, Phase 1)
 * Orchestre l'orbe visuelle et le fil de conversation, pilote l'envoi vocal/texte
 * et l'exécution des intents via `VoiceService`. La session (fil, entités, follow-up)
 * survit à la fermeture de l'overlay — seule la visibilité change.
 *
 * MOKA-206 Phase 3 : orchestre aussi les flows guidés par boutons
 * (`VoiceGuidedFlowService`) — chips d'accueil, palette « / », boutons de
 * réponse rapide sous les bulles assistant — et la clarification "projet
 * manquant" (Lot C/H), qui partage le même point d'entrée `onOptionSelected`.
 *
 * Lot E (retour terrain) : le panneau orbe (`app-voice-orb`) a été retiré —
 * jugé disproportionné pour ce qu'il affichait. Les états écoute/parole sont
 * désormais portés par `.chat-thread__mic-btn` dans `VoiceChatThreadComponent`
 * (`isRecording`/`isSpeaking` en `input()`), ce composant ne pilote plus qu'un
 * panneau de chat pleine largeur.
 */
@Component({
  selector: 'app-voice-assistant-overlay',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgIcon, RouterLink, CdkTrapFocus, VoiceChatThreadComponent, VoiceSessionHistoryComponent, TutorialButtonComponent],
  viewProviders: [provideIcons({ iconoirXmark, iconoirClockRotateRight })],
  host: {
    '(document:keydown.escape)': 'onEscape()'
  },
  templateUrl: './voice-assistant-overlay.component.html',
  styleUrl: './voice-assistant-overlay.component.scss'
})
export class VoiceAssistantOverlayComponent {
  voiceService = inject(VoiceService);
  voiceSession = inject(VoiceSessionService);
  guidedFlow = inject(VoiceGuidedFlowService);
  private router = inject(Router);
  private briefing = inject(VoiceBriefingService);
  private overlay = inject(VoiceOverlayService);
  private tutorials = inject(TutorialService);
  protected readonly assistantEnabled = inject(SubscriptionFeaturesService).assistantEnabled;
  protected readonly tour = ASSISTANT_OVERLAY_TOUR;

  /** Visite guidée en cours : le panneau passe sous le voile de Driver.js (z-index 10000) pour être mis en lumière. */
  protected readonly touring = computed(() => this.tutorials.activeId() === ASSISTANT_OVERLAY_TOUR.id);

  open = input(false);
  closed = output<void>();

  private readonly thread = viewChild(VoiceChatThreadComponent);
  private readonly injector = inject(Injector);
  /** Élément qui avait le focus avant l'ouverture : il le retrouve à la fermeture. */
  private returnFocusTo: HTMLElement | null = null;

  /**
   * Zone réellement visible sur mobile (MOKA-363) : quand le clavier iOS s'ouvre, `100vh`
   * reste la hauteur de l'écran et le champ de saisie passait sous le clavier. On suit
   * `visualViewport` tant que le panneau est ouvert.
   */
  protected readonly viewportHeight = signal<string | null>(null);
  protected readonly viewportTop = signal<string | null>(null);

  constructor() {
    // L'accroche se charge à la PREMIÈRE ouverture seulement (le service garde
    // la trace) : inutile de réinterroger la base à chaque va-et-vient.
    effect(() => {
      if (this.open()) void this.briefing.load();
    });

    // Focus : dans le champ à l'ouverture, rendu à l'élément d'origine à la fermeture.
    let wasOpen = false;
    effect(() => {
      const isOpen = this.open();
      if (isOpen && !wasOpen) {
        this.returnFocusTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
        afterNextRender(() => this.thread()?.focusInput(), { injector: this.injector });
      } else if (!isOpen && wasOpen) {
        const target = this.returnFocusTo;
        this.returnFocusTo = null;
        if (target?.isConnected) target.focus();
      }
      wasOpen = isOpen;
    });

    // Clavier mobile : hauteur et position calées sur la zone visible.
    const vv = typeof window !== 'undefined' ? window.visualViewport : null;
    if (vv) {
      const sync = () => {
        if (!this.open() || window.innerWidth > 768) {
          this.viewportHeight.set(null);
          this.viewportTop.set(null);
          return;
        }
        this.viewportHeight.set(`${Math.round(vv.height)}px`);
        this.viewportTop.set(`${Math.round(vv.offsetTop)}px`);
      };
      vv.addEventListener('resize', sync);
      vv.addEventListener('scroll', sync);
      effect(() => { this.open(); sync(); });
      inject(DestroyRef).onDestroy(() => {
        vv.removeEventListener('resize', sync);
        vv.removeEventListener('scroll', sync);
      });
    }

    // Phrase saisie hors de l'overlay (accueil) : envoyée une seule fois à l'ouverture.
    effect(() => {
      const text = this.overlay.pendingText();
      if (!this.open() || !text) return;
      this.overlay.pendingText.set(null);
      void this.onSendText(text);
    });
  }

  showHistory = signal(false);
  historyError = signal<string | null>(null);

  close(): void {
    this.closed.emit();
  }

  onEscape(): void {
    // Pendant la visite guidée, Échap ferme la visite (Driver.js), pas l'assistant.
    if (this.open() && !this.touring()) {
      this.close();
    }
  }

  onBackdropClick(): void {
    this.close();
  }

  /**
   * Séquencement Lot F : `guidedFlow.submitText` a la priorité — s'il gère le
   * texte (flow actif à étape texte libre), on s'arrête là. Sinon (`false` :
   * aucun flow actif, ou étape à choix fermé, ou clarification Lot C annulée
   * par une saisie libre), `guidedFlow.cancel()` est appelé INCONDITIONNELLEMENT
   * (no-op si rien n'était actif) puis la commande part normalement vers GPT —
   * c'est la règle d'annulation : taper autre chose annule proprement tout
   * flow/clarification en cours.
   */
  async onSendText(text: string): Promise<void> {
    const handled = await this.guidedFlow.submitText(text);
    if (handled) return;

    this.guidedFlow.cancel();

    const result = await this.voiceService.sendTextCommand(text);
    if (result) {
      await this.voiceService.executeAndRespond(result, 'text');
    }
  }

  async onStartVoice(): Promise<void> {
    const result = await this.voiceService.startCommand();
    if (result) {
      await this.voiceService.executeAndRespond(result, 'voice');
    } else if (this.voiceService.hasError()) {
      // Micro refusé ou absent : on passe à l'écrit, le message dit pourquoi (MOKA-363).
      this.thread()?.focusInput();
    }
  }

  /** « Réessayer » : renvoie la dernière demande écrite, sans doubler sa bulle (MOKA-363). */
  async onRetry(): Promise<void> {
    const text = this.voiceService.lastFailedText();
    if (!text || this.voiceService.isProcessing()) return;
    const result = await this.voiceService.sendTextCommand(text, { replay: true });
    if (result) {
      await this.voiceService.executeAndRespond(result, 'text');
    }
  }

  /**
   * Arrêt manuel de l'enregistrement (Lot E, addendum toggle micro). Rejoint
   * exactement la même chaîne que l'arrêt naturel (silence détecté / durée
   * max) : `AudioCaptureService` résout la même Promise via `onstop` quel que
   * soit le déclencheur — `startCommand()` (déjà en attente dans
   * `onStartVoice`) se résout alors normalement, rien à dupliquer ici.
   */
  onStopVoice(): void {
    this.voiceService.stopRecording();
  }

  /** Action rapide choisie (chip d'accueil ou palette « / ») → démarre le flow guidé correspondant */
  async onQuickAction(flow: QuickActionFlow): Promise<void> {
    await this.guidedFlow.start(flow);
  }

  /**
   * Bouton de réponse rapide cliqué sous une bulle assistant : trois sources
   * possibles, dans cet ordre de priorité.
   * 1. Clarification "projet manquant" en attente → la résout (ne peut être
   *    active qu'après l'échec d'un intent GPT direct, jamais en même temps
   *    qu'un flow guidé dans cette Phase 3, mais vérifier en premier reste la
   *    garde la plus sûre).
   * 2. Chips de suggestion du message "je ne sais pas faire ça" (Lot E,
   *    `VoiceService.#respondUnsupported`) : AUCUN flow actif à ce moment
   *    (elles ne répondent à aucune étape), et leur `option.id` est un
   *    `QuickActionFlow` valide — démarrent le flow correspondant, exactement
   *    comme un clic sur une chip d'accueil (même message user ajouté ici,
   *    pour que le clic laisse une trace dans le fil comme tout autre bouton
   *    de réponse rapide).
   * 3. Sinon, réponse normale à l'étape courante d'un flow guidé actif.
   */
  /**
   * Clic sur une entité citée (tâche listée…) : on navigue et on ferme
   * l'overlay — laisser la conversation ouverte par-dessus la page demandée
   * serait absurde. La session survit, elle est reprise à la réouverture.
   */
  onLinkSelected(link: VoiceMessageLink): void {
    void this.router.navigateByUrl(link.route);
    this.close();
  }

  async onOptionSelected(option: VoiceMessageOption): Promise<void> {
    // Carte « Confirmer / Annuler » (MOKA-299) : testée en premier, elle ne
    // concerne aucun flow guidé (celui de suppression a sa propre confirmation).
    if (this.voiceService.isConfirmationOption(option.id)) {
      await this.voiceService.resolveConfirmation(option.id);
      return;
    }

    // Alternative proposée après un échec récupérable (créneau de salle pris) :
    // testée en premier, elle porte son propre intent et n'appartient à aucun flow.
    if (this.voiceService.hasSuggestedAction(option.id)) {
      await this.voiceService.runSuggestedAction(option.id);
      return;
    }

    if (this.guidedFlow.pendingClarification()) {
      await this.guidedFlow.resolveClarification(option);
      return;
    }

    if (!this.guidedFlow.activeFlow() && this.#isQuickActionFlowId(option.id)) {
      this.voiceSession.addMessage('user', option.label, 'text', undefined, false);
      await this.guidedFlow.start(option.id);
      return;
    }

    await this.guidedFlow.submitOption(option);
  }

  #isQuickActionFlowId(id: string): id is QuickActionFlow {
    return QUICK_ACTIONS.some(action => action.id === id);
  }

  toggleHistory(): void {
    this.showHistory.update(value => !value);
    this.historyError.set(null);
  }

  /**
   * Reprise de session depuis le panneau d'historique. `loadSession` est la
   * seule opération de persistance dont l'échec doit être visible (chargement
   * explicite demandé par l'utilisateur, contrairement aux écritures
   * fire-and-forget de `VoiceSessionService`) — d'où le try/catch local.
   */
  async onHistorySessionSelected(sessionId: string): Promise<void> {
    try {
      await this.voiceSession.loadSession(sessionId);
      this.historyError.set(null);
      this.showHistory.set(false);
    } catch (err) {
      console.warn('[VoiceAssistantOverlay] Échec du chargement de la session', err);
      this.historyError.set('Impossible de charger cette conversation.');
    }
  }

  onHistoryNewConversation(): void {
    this.voiceSession.startNewConversation();
    this.historyError.set(null);
    this.showHistory.set(false);
  }

  /**
   * Lot D (suppression de session) : la session supprimée était la session
   * active — `VoiceSessionService.deleteSession` a déjà fait son propre
   * `reset()` interne, il ne reste qu'à refermer le panneau d'historique
   * (même geste que `onHistoryNewConversation`, sans rappeler `reset()`).
   */
  onHistoryActiveSessionDeleted(): void {
    this.historyError.set(null);
    this.showHistory.set(false);
  }
}
